/**
 * OTP Input component with 4 separate boxes.
 * 
 * Implements Figma design node 36:59 - mobile OTP verification screen.
 * Features:
 * - 4 separate 80x80 input boxes with 12px radius
 * - Auto-advance on digit entry
 * - Backspace moves to previous box
 * - Paste support for full code (from any box)
 * - Numeric input only
 * - Completion callback emitted directly in handlers (not via useEffect)
 * 
 * Security notes:
 * - Numeric-only input prevents non-digit characters
 * - No autocomplete on individual boxes (but parent form handles one-time-code)
 */
import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent, forwardRef, useImperativeHandle } from 'react';

export interface OTPInputHandle {
  focus: () => void;
  clear: () => void;
  getValue: () => string;
}

interface OTPInputProps {
  length?: number;
  disabled?: boolean;
  onChange?: (code: string) => void;
  error?: boolean;
  autoFocus?: boolean;
}

export const OTPInput = forwardRef<OTPInputHandle, OTPInputProps>(({
  length = 4,
  disabled = false,
  onChange,
  error = false,
  autoFocus = true,
}, ref) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      const firstEmptyIndex = values.findIndex(v => v === '');
      const focusIndex = firstEmptyIndex === -1 ? length - 1 : firstEmptyIndex;
      inputRefs.current[focusIndex]?.focus();
    },
    clear: () => {
      setValues(Array(length).fill(''));
      inputRefs.current[0]?.focus();
    },
    getValue: () => values.join(''),
  }), [length, values]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus, disabled]);

  // Helper to emit onChange callback - called directly from handlers
  const emitOnChange = (newValues: string[]) => {
    if (onChange) {
      onChange(newValues.join(''));
    }
  };

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const value = e.target.value;
    const prevCode = values.join('');
    
    // Extract only digits from input
    const digit = value.replace(/\D/g, '');
    
    // Check if this is a paste (multiple new digits being entered)
    // A paste typically adds multiple characters beyond what we expect
    // We detect this by checking if the new value has more than one digit
    // AND that the digits don't match a simple single-digit entry
    if (digit.length > 1 || (digit.length === 1 && values[index] !== '')) {
      // Handle paste - fill current and subsequent boxes
      const newValues = [...values];
      
      // Fill starting from current index
      for (let i = 0; i < digit.length && index + i < length; i++) {
        newValues[index + i] = digit[i];
      }
      
      setValues(newValues);
      emitOnChange(newValues);
      
      // Focus last filled or next empty
      const focusIndex = Math.min(index + digit.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
      return;
    }
    
    // Single digit entry
    const newValues = [...values];
    newValues[index] = digit;
    setValues(newValues);
    emitOnChange(newValues);

    // Auto-advance to next input (only for single digit entries)
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (values[index] === '' && index > 0) {
        // Move to previous input and clear it
        const newValues = [...values];
        newValues[index - 1] = '';
        setValues(newValues);
        emitOnChange(newValues);
        inputRefs.current[index - 1]?.focus();
      } else if (values[index] !== '') {
        // Clear current input
        const newValues = [...values];
        newValues[index] = '';
        setValues(newValues);
        emitOnChange(newValues);
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'Enter') {
      // Allow form submission
      e.currentTarget.form?.requestSubmit();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (disabled) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, length);
    
    if (digits.length > 0) {
      const newValues = [...values];
      for (let i = 0; i < digits.length; i++) {
        newValues[i] = digits[i];
      }
      setValues(newValues);
      emitOnChange(newValues);
      
      // Focus last filled or next empty
      const focusIndex = Math.min(digits.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleFocus = (index: number) => {
    // Select content on focus
    inputRefs.current[index]?.select();
  };

  return (
    <div 
      className="otp-input-container"
      role="group"
      aria-label="Verification code input"
    >
      {values.map((value, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={2}
          value={value}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={`otp-box ${error ? 'otp-box-error' : ''}`}
          aria-label={`Digit ${index + 1} of ${length}`}
          autoComplete="off"
          data-testid={`otp-digit-${index + 1}`}
        />
      ))}
    </div>
  );
});

OTPInput.displayName = 'OTPInput';
